import { RemoteConfigProperties } from "../../../..";
import { NotImplementedError } from "../../../../errors";
import { PromiseHandler } from "../../../../handlers";
import { attributionQueryDimensionsToModel, createDummyQueryFilters, toAttributionApiRequest, toAttributionModelRows } from "../../../../models/looker/modules/attribution";
import { toEmbedHash } from "../../../../models/looker/query";
import Advertiser from "./filters/Advertiser";
import Campaign from "./filters/Campaign";
import Commercial from "./filters/Commercial";
import Pixel from "./filters/Pixel";
import Scheduling from "./scheduling";
import { buildAttributionQuery } from "./utils";
class Attribution extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-attribution");
        this.filters = {
            advertiser: new Advertiser(sdk),
            campaign: new Campaign(sdk),
            pixel: new Pixel(sdk),
            commercial: new Commercial(sdk)
        };
        this.schedule = new Scheduling(sdk);
    }
    async findItems(filters) {
        if (this.sdk.featureFlags.isFlagEnabled(RemoteConfigProperties.REPORTING_API_ATTRIBUTION_QUERY)) {
            return this.findItemsFromReportingApi(filters);
        }
        const isBlacklistEnabled = this.sdk.featureFlags.isFlagEnabled(RemoteConfigProperties.CONVERSIONS_BLACKLIST);
        const isAdvertiserAttrEnabled = this.sdk.featureFlags.isFlagEnabled(RemoteConfigProperties.SIMPLIFIED_ATTRIBUTION_INSIGHTS_ADVERTISER);
        const isUniversalPixelConversionEventsEnabled = this.sdk.featureFlags.isFlagEnabled(RemoteConfigProperties.UNIVERSAL_PIXEL_CONVERSION_EVENTS);
        const queryBody = buildAttributionQuery(filters, {
            isBlacklistEnabled,
            isAdvertiserAttrEnabled,
            isUniversalPixelConversionEventsEnabled
        });
        const hashKey = `looker:${await this.sdk.looker.query.hashQuery(queryBody)}`;
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: data.map((attribution) => attributionQueryDimensionsToModel(filters.fields || [], attribution)),
                queryMeta
            }
        ])
            .catch(() => {
            throw new Error("Failed to fetch attribution data");
        }));
    }
    /**
     * The same rows through the reporting API instead of the Looker SDK, in the
     * same shape, so no caller can tell which source answered.
     *
     * The three feature flags the Looker path reads are deliberately absent: the
     * endpoint reads them itself and applies the parameters they gate, and they
     * are not filter keys, so sending them would earn a 400.
     *
     * A failure is surfaced rather than retried through Looker, so a broken
     * endpoint cannot present as working.
     */
    async findItemsFromReportingApi(filters) {
        const { endpoint, request } = toAttributionApiRequest(filters);
        // Namespaced by transport: the cache outlives a mid-session flag flip, and
        // persistent caching outlives the session, so an unnamespaced key could
        // serve rows fetched through the other transport.
        //
        // The endpoint is hashed alongside the body because it is not derivable
        // from it: linear and overlap emit the same filter keys but for
        // `lineitem_ids`, so two calls that differ only in explore can otherwise
        // collide — and the second would be served the first's rows rather than
        // the refusal it had earned.
        const hashKey = `api:${await this.sdk.looker.query.hashQuery({ endpoint, request })}`;
        return this.cache.promise(hashKey, () => this.sdk.looker.reporting
            .query(endpoint, request, "Failed to fetch attribution data.")
            .then(({ data, query }) => [
            {
                data: toAttributionModelRows(data),
                queryMeta: { embedHash: toEmbedHash(query?.share_url) }
            }
        ]));
    }
    async getURLFiltersQuerySlug(filters) {
        const queryFilters = createDummyQueryFilters(filters);
        return this.sdk.looker.query
            .create({
            view: "fake_view_for_filter_storage",
            model: "client_analytics",
            filters: queryFilters
        })
            .then((query) => {
            return query.client_id || "";
        });
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
export default Attribution;
//# sourceMappingURL=index.js.map